import {fuzzyScore, NO_MATCH} from '@github-ui/fuzzy-score/fuzzy-score'
import {ArrowSwitchIcon, CopilotIcon, PlusCircleIcon} from '@primer/octicons-react'
import {hasMatch} from 'fzy.js'

import {DEFAULT_FILTER_PRIORITY, INDETERMINANT, USER_FILTERS} from '../constants/filter-constants'
import type {FilterQuery} from '../filter-query'
import {
  type AnyBlock,
  type ARIAFilterSuggestion,
  AvatarType,
  type FilterConfig,
  type FilterKey,
  type FilterProvider,
  FilterProviderType,
  type FilterValueData,
  FilterValueType,
  type IndexedBlockValueItem,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  type UnrankedARIAFilterSuggestion,
  type ValueRowProps,
} from '../types'
import {
  caseInsensitiveStringCompare,
  getDefaultValueVisualDescriptors,
  getFilterValue,
  getLastFilterBlockValue,
  getUnescapedFilterValue,
  isFilterBlock,
} from '../utils'
import {AsyncFilterProvider} from './async'

const USER_SUGGESTION_ENDPOINT = '/_filter/users'
const USER_VALIDATION_ENDPOINT = '/_filter/users/validate'
const AT_ME_VALUE = '@me'
export const AT_COPILOT_VALUE = '@copilot'

const AT_ME_SUGGESTION = {
  type: FilterValueType.Value,
  value: AT_ME_VALUE,
  ariaLabel: `${AT_ME_VALUE}, Signed-in user`,
  displayName: 'Me',
  description: 'Signed-in user',
  inlineDescription: true,
  priority: 1,
  icon: ArrowSwitchIcon,
  iconColor: 'var(--fgColor-done, var(--color-done-fg))',
}

const getCopilotSuggestion = (displayName: string, avatarUrl: string | undefined) => ({
  type: FilterValueType.Value,
  value: AT_COPILOT_VALUE,
  ariaLabel: `${AT_COPILOT_VALUE}, Your AI pair programmer`,
  displayName,
  description: 'Your AI pair programmer',
  inlineDescription: true,
  priority: 1,
  icon: CopilotIcon,
  avatarUrl: avatarUrl ? {url: avatarUrl, type: AvatarType.User} : undefined,
  iconColor: 'var(--fgColor-done, var(--color-done-fg))',
})

const WILDCARD_HAS_VALUE = '*'
const getHasValueSuggestion = (displayName: string) => ({
  type: FilterValueType.Value,
  value: WILDCARD_HAS_VALUE,
  displayName: `Has ${displayName.toLocaleLowerCase()}`,
  ariaLabel: `Has ${displayName}, ${displayName}`,
  priority: 1,
  icon: PlusCircleIcon,
})

export type User = {
  name: string
  login: string
  avatarUrl?: string
  isCopilot?: boolean
}

export type UserFilterParams = {
  showAtMe?: boolean
  showAtCopilot?: boolean
  currentUserLogin?: string
  currentUserAvatarUrl?: string
  repositoryScope?: string
  includeBots?: boolean
  suggestPullRequestAuthors?: boolean
}

export class BaseUserFilterProvider extends AsyncFilterProvider<User> implements FilterProvider {
  showAtMe: boolean
  showAtCopilot: boolean
  currentUserLogin?: string
  currentUserAvatarUrl?: string
  includeBots?: boolean
  copilotQueryParamKey?:
    | 'show_assignee_copilot'
    | 'show_author_copilot'
    | 'show_pull_request_reviewer_copilot'
    | 'show_involves_copilot'

  constructor(filterKey: FilterKey, filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(filterKey, options)
    this.suggestionEndpoint = USER_SUGGESTION_ENDPOINT
    this.validationEndpoint = USER_VALIDATION_ENDPOINT
    this.currentUserLogin = filterParams?.currentUserLogin
    this.currentUserAvatarUrl = filterParams?.currentUserAvatarUrl
    this.includeBots = filterParams?.includeBots ?? false
    this.type = FilterProviderType.User
    this.showAtMe = filterParams?.showAtMe ?? true
    this.showAtCopilot = filterParams?.showAtCopilot ?? false
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    if (this.includeBots) {
      filterQuery.addQueryParam('include_bots', '1')
    }
    if (this.copilotQueryParamKey) {
      filterQuery.addQueryParam(this.copilotQueryParamKey, '1')
    }

    const suggestions =
      (await this.processSuggestions(filterQuery, filterBlock, this.processSuggestion.bind(this), caretIndex)) ?? []
    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const hasNoValueRow = suggestions[0]?.type === FilterValueType.NoValue
    let spliceIndex = hasNoValueRow ? 1 : 0
    if (
      this.showAtMe &&
      (lastValue === '' || hasMatch(lastValue, AT_ME_VALUE)) &&
      isFilterBlock(filterBlock) &&
      filterBlock.value?.values.findIndex(v =>
        caseInsensitiveStringCompare(getFilterValue(v.value) ?? '', AT_ME_VALUE),
      ) < 0 &&
      this.allowsFilterValues()
    ) {
      suggestions.splice(spliceIndex, 0, AT_ME_SUGGESTION)
      spliceIndex++
    }
    if (
      (lastValue === '' || hasMatch(lastValue, AT_COPILOT_VALUE)) &&
      isFilterBlock(filterBlock) &&
      filterBlock.value?.values.findIndex(v =>
        caseInsensitiveStringCompare(getFilterValue(v.value) ?? '', AT_COPILOT_VALUE),
      ) < 0 &&
      this.allowsFilterValues()
    ) {
      const copilotIndex = suggestions.findIndex(v =>
        caseInsensitiveStringCompare(getFilterValue(v.value) ?? '', AT_COPILOT_VALUE),
      )
      if (copilotIndex > -1) {
        const [copilotSuggestion] = suggestions.splice(copilotIndex, 1)
        if (this.showAtCopilot && copilotSuggestion) {
          // Inject the Copilot suggestion to after @me
          suggestions.splice(spliceIndex, 0, copilotSuggestion)
          spliceIndex++
        }
      }
    }
    return suggestions
  }

  protected processSuggestion(user: User, query: string): ARIAFilterSuggestion {
    const {login, name, avatarUrl, isCopilot} = user

    if (isCopilot) {
      return getCopilotSuggestion(login, avatarUrl)
    }

    return this.rankSuggestion(query, {
      type: FilterValueType.Value,
      value: login,
      ariaLabel: `${login}, ${name}, ${this.displayName}`,
      description: name,
      inlineDescription: true,
      icon: !avatarUrl ? this.icon : undefined,
      avatar: avatarUrl ? {url: avatarUrl, type: AvatarType.User} : undefined,
    })
  }

  override rankSuggestion(query: string, suggestion: UnrankedARIAFilterSuggestion): ARIAFilterSuggestion {
    const {value, description: name} = suggestion
    const login = getUnescapedFilterValue(value)
    let priority = DEFAULT_FILTER_PRIORITY

    if (query) {
      // Weight the score more heavily by login, then influence by name
      const loginWeighting = 0.75
      if (login) {
        const loginScore = fuzzyScore(query, login)
        priority -= (loginScore !== NO_MATCH ? loginScore : 1) * loginWeighting
      }

      if (name) {
        const nameScore = fuzzyScore(query, name)
        priority -= (nameScore !== NO_MATCH ? nameScore : 1) * (1 - loginWeighting)
      }
    }

    // We want to have @me at the top if it's included
    if (caseInsensitiveStringCompare(login ?? '', AT_ME_VALUE)) {
      priority = 1
    }

    return {
      ...suggestion,
      priority,
    }
  }

  override getMetaSuggestions(value: string): ARIAFilterSuggestion[] {
    const suggestions: ARIAFilterSuggestion[] = []
    // Prefix matching is case-insensitive so completed meta values (e.g. `@Me`) still resolve to
    // their canonical suggestion for validation, matching the case-insensitive equality checks.
    const normalizedValue = value.toLowerCase()
    if (this.showAtMe && AT_ME_VALUE.startsWith(normalizedValue)) {
      suggestions.push(AT_ME_SUGGESTION)
    }
    if (this.options.filterTypes.hasValue && WILDCARD_HAS_VALUE.startsWith(normalizedValue)) {
      suggestions.push(getHasValueSuggestion(this.displayName))
    }

    return suggestions
  }

  override validateValue(
    filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    user: User | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)

    if (caseInsensitiveStringCompare(extractedValue ?? '', AT_ME_VALUE)) {
      return {
        avatar: this.currentUserAvatarUrl ? {url: this.currentUserAvatarUrl, type: AvatarType.User} : undefined,
        value: extractedValue ?? undefined,
      }
    }
    if (this.options.filterTypes.hasValue && extractedValue === WILDCARD_HAS_VALUE) {
      return {
        avatar: undefined,
        value: extractedValue,
      }
    }
    if (user && this.showAtCopilot && caseInsensitiveStringCompare(extractedValue ?? '', AT_COPILOT_VALUE)) {
      return {
        avatar: undefined,
        value: extractedValue ?? undefined,
      }
    }

    if (user && extractedValue)
      return {
        avatar: user.avatarUrl ? {url: user.avatarUrl, type: AvatarType.User} : undefined,
        value: extractedValue,
        displayName: user.name,
      }

    if (!filterQuery.context.repo && !filterQuery.context.org) {
      return {
        value: INDETERMINANT,
      }
    }

    return false
  }

  override validateFilterValue(
    value: string,
    fetchParams?: URLSearchParams,
    filterQuery?: FilterQuery,
  ): Promise<User | Error | null> {
    const params = new URLSearchParams(fetchParams)
    if (this.copilotQueryParamKey) {
      params.append(this.copilotQueryParamKey, '1')
    }
    return super.validateFilterValue(value, params, filterQuery)
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    const customLeadingVisual = this.options.renderValueVisual?.(value)
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      descriptionVariant: 'inline',
      leadingVisual: this.options.renderValueVisual
        ? (customLeadingVisual ?? null)
        : getDefaultValueVisualDescriptors(value, this.icon),
    }
  }
}

export class AssigneeFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.assignee, filterParams, options)
    this.copilotQueryParamKey = filterParams?.showAtCopilot ? 'show_assignee_copilot' : undefined
  }
}

export class AuthorFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {multiValue: false, valueless: false}}
    super(
      USER_FILTERS.author,
      {
        ...filterParams,
        includeBots: true,
      },
      {
        ...defaultOptions,
        ...options,
        filterTypes: {...defaultOptions.filterTypes, ...options?.filterTypes},
      },
    )
    this.copilotQueryParamKey = filterParams?.showAtCopilot ? 'show_author_copilot' : undefined
    if (filterParams?.suggestPullRequestAuthors && filterParams.repositoryScope) {
      this.providerContext = new URLSearchParams({repo: filterParams.repositoryScope})
      this.providerContext.set('pull_request_authors', '1')
      this.showAtMe = false
    }
  }
}

export class CommenterFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.commenter, filterParams, options)
  }
}

export class InvolvesFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.involves, filterParams, options)
    this.copilotQueryParamKey = filterParams?.showAtCopilot ? 'show_involves_copilot' : undefined
  }
}

export class MentionsFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.mentions, filterParams, options)
  }
}

export class ReviewedByFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.reviewedBy, filterParams, options)
    this.copilotQueryParamKey = filterParams?.showAtCopilot ? 'show_pull_request_reviewer_copilot' : undefined
  }
}

export class ReviewRequestedFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.reviewRequested, filterParams, options)
    this.copilotQueryParamKey = filterParams?.showAtCopilot ? 'show_pull_request_reviewer_copilot' : undefined
  }
}

export class UserFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.user, filterParams, options)
  }
}

export class UserReviewRequestedFilterProvider extends BaseUserFilterProvider {
  constructor(filterParams?: UserFilterParams, options?: SuppliedFilterProviderOptions) {
    super(USER_FILTERS.userReviewRequested, filterParams, options)
  }
}
